import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Calendar, LogOut, Briefcase, UserCog, ChevronLeft, ChevronRight, Menu, X, Settings, Clock } from 'lucide-react';

export default function Sidebar({ user, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'Admin';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when navigating
  const handleNavClick = () => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const adminNavItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/late-tracking', icon: Clock, label: 'Late Tracking' },
  ];

  const adminQuickActions = [
    { to: '/admin/users', icon: UserCog, label: 'User Management' },
    { to: '/admin/employees', icon: Users, label: 'Manage Balances' },
    { to: '/admin/holidays', icon: Calendar, label: 'Company Holidays' },
    { to: '/admin/reports', icon: FileText, label: 'View Reports' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const employeeNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/leave-history', icon: FileText, label: 'Leave History' },
    { to: '/apply-leave', icon: Calendar, label: 'Apply Leave' },
  ];

  const navItems = isAdmin ? adminNavItems : employeeNavItems;
  const quickActions = isAdmin ? adminQuickActions : [];

  const sidebarContent = (
    <>
      {/* Logo & Collapse Button */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${isCollapsed ? 'w-8 h-8' : 'w-10 h-10'} bg-primary rounded-lg flex items-center justify-center flex-shrink-0 transition-all`}>
              <Briefcase className={`${isCollapsed ? 'w-4 h-4' : 'w-5 h-5 md:w-6 md:h-6'} text-white`} />
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base md:text-lg text-foreground">LeavFlow</h1>
                <p className="text-xs text-muted-foreground hidden md:block">Leave Management</p>
              </div>
            )}
          </div>
          
          {/* Desktop Collapse Button */}
          {!isMobile && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
          
          {/* Mobile Close Button */}
          {isMobile && (
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* User Profile */}
      <div className={`${isCollapsed ? 'p-2' : 'p-4'} border-b border-border`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
          <div className={`${isCollapsed ? 'w-8 h-8' : 'w-10 h-10'} bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0`}>
            <span className={`text-primary font-semibold ${isCollapsed ? 'text-xs' : 'text-sm'}`}>
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-sm text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.role}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-2 md:p-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              title={isCollapsed ? item.label : ''}
              className={({ isActive }) =>
                `flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg font-heading text-sm transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {quickActions.length > 0 && (
          <div className="mt-6">
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">
                Quick Actions
              </p>
            )}
            <nav className="space-y-1">
              {quickActions.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={handleNavClick}
                  data-testid={`quick-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={isCollapsed ? item.label : ''}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg font-body text-sm transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>

      {/* Logout */}
      <div className={`${isCollapsed ? 'p-2' : 'p-4'} border-t border-border`}>
        <button
          onClick={onLogout}
          data-testid="sidebar-logout"
          title={isCollapsed ? 'Logout' : ''}
          className={`flex items-center justify-center ${isCollapsed ? '' : 'gap-2'} px-3 py-2.5 md:py-3 rounded-lg font-heading text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors w-full`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && !isMobileOpen && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-border hover:bg-muted transition-colors"
          data-testid="mobile-menu-button"
        >
          <Menu className="w-6 h-6 text-foreground" />
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          ${isCollapsed ? 'w-16' : 'w-64'} 
          bg-white border-r border-border h-screen fixed left-0 top-0 flex flex-col z-50
          transition-all duration-300 ease-in-out
          ${isMobile ? (isMobileOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        `}
      >
        {sidebarContent}
      </div>

      {/* Spacer for content */}
      {!isMobile && (
        <div className={`${isCollapsed ? 'w-16' : 'w-64'} flex-shrink-0 transition-all duration-300`} />
      )}
    </>
  );
}
